// NotificationsPanel.jsx — FR-19 in-app notification center
// Rendered as a fixed dropdown panel anchored below the bell icon.
// Opened/closed by the parent; receives onClose + onCountChange callbacks.
import React from "react"
import { Icon, useT } from "../shared"
import { Btn } from "../shared/components"
import { useApp } from "../context/AppContext"
import {
  listNotifications,
  markNotifRead,
  markAllNotifsRead,
} from "../api/notifications.api"

// Icon & color per notification type
const TYPE_META = {
  TRAINER_ASSIGNED:  { icon: "users",    color: "var(--program-color)" },
  SESSION_REMINDER:  { icon: "calendar", color: "var(--amber)" },
  ESSAY_GRADED:      { icon: "check",    color: "var(--success)" },
  CERT_READY:        { icon: "cert",     color: "var(--success)" },
  TEST_AVAILABLE:    { icon: "check",    color: "var(--program-color)" },
  DOC_APPROVED:      { icon: "layers",   color: "var(--success)" },
  DOC_REJECTED:      { icon: "layers",   color: "var(--danger)" },
  SESSION_CHANGED:   { icon: "calendar", color: "var(--amber)" },
  SESSION_CANCELLED: { icon: "calendar", color: "var(--danger)" },
  APPRAISAL_ASSIGNED:  { icon: "check", color: "var(--program-color)" },
  APPRAISAL_SUBMITTED: { icon: "check", color: "var(--success)" },
  // Kedua tipe ini sudah ada di enum Prisma sejak lama tetapi tidak pernah
  // terdaftar di sini, jadi penerimanya melihat lonceng generik yang membawanya
  // ke dasbor alih-alih ke tempat kejadiannya. Sekalian diperbaiki.
  ATTENDANCE_RECORDED: { icon: "qr",    color: "var(--program-color)" },
  TEST_SCORE_ENTERED:  { icon: "chart", color: "var(--success)" },
}

// Navigate to the relevant screen for each notification type
function actionScreenForType(type) {
  switch (type) {
    case "TRAINER_ASSIGNED":   return "dashboard"
    case "SESSION_REMINDER":   return "sessions"
    case "ESSAY_GRADED":       return "posttest"
    case "CERT_READY":         return "certificates"
    case "TEST_AVAILABLE":     return "posttest"
    case "DOC_APPROVED":       return "doc-library"
    case "DOC_REJECTED":       return "doc-library"
    case "SESSION_CHANGED":    return "sessions"
    case "SESSION_CANCELLED":  return "sessions"
    case "APPRAISAL_ASSIGNED":  return "penilaian"
    case "APPRAISAL_SUBMITTED": return "penilaian"
    case "ATTENDANCE_RECORDED": return "qr"
    case "TEST_SCORE_ENTERED":  return "learning-gain"
    default:                   return "dashboard"
  }
}

// Relative time string (e.g. "2 jam lalu" / "3 days ago")
function relTime(iso, lang) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days  = Math.floor(diff / 86400000)
  if (lang === "id") {
    if (mins  < 1)  return "Baru saja"
    if (mins  < 60) return `${mins} mnt lalu`
    if (hours < 24) return `${hours} jam lalu`
    return `${days} hari lalu`
  } else {
    if (mins  < 1)  return "Just now"
    if (mins  < 60) return `${mins}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }
}

export default function NotificationsPanel({ onClose, onCountChange }) {
  const { lang, navigate } = useApp()
  const t = useT()
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const panelRef = React.useRef(null)

  // Load on mount
  React.useEffect(() => {
    setLoading(true)
    listNotifications()
      .then((data) => {
        setItems(data.items || [])
        onCountChange?.(data.unread ?? 0)
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  React.useEffect(() => {
    function handle(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose?.()
      }
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [onClose])

  const handleMarkAllRead = async () => {
    try {
      await markAllNotifsRead()
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })))
      onCountChange?.(0)
    } catch { /* non-fatal */ }
  }

  const handleClickItem = async (item) => {
    // Mark as read
    if (!item.read_at) {
      try {
        await markNotifRead(item.id)
        setItems((prev) =>
          prev.map((n) => n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n)
        )
        const newUnread = items.filter((n) => !n.read_at && n.id !== item.id).length
        onCountChange?.(newUnread)
      } catch { /* non-fatal */ }
    }
    // Navigate to relevant screen
    navigate(actionScreenForType(item.type))
    onClose?.()
  }

  const unread = items.filter((n) => !n.read_at).length

  return (
    <div
      ref={panelRef}
      className="notif-panel"
    >
      {/* Header */}
      <div className="notif-panel-header">
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {lang === "id" ? "Notifikasi" : "Notifications"}
          {unread > 0 && (
            <span className="notif-count-chip">{unread}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {unread > 0 && (
            <Btn variant="ghost" size="sm" onClick={handleMarkAllRead}>
              {lang === "id" ? "Tandai semua terbaca" : "Mark all read"}
            </Btn>
          )}
          <button className="icon-btn sm" onClick={onClose} aria-label="Close">
            <Icon name="close" size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="notif-panel-body">
        {loading ? (
          <div className="notif-empty">{lang === "id" ? "Memuat…" : "Loading…"}</div>
        ) : items.length === 0 ? (
          <div className="notif-empty">
            <Icon name="bell" size={24} style={{ opacity: 0.3 }} />
            <span>{lang === "id" ? "Belum ada notifikasi" : "No notifications yet"}</span>
          </div>
        ) : (
          <ul className="notif-list">
            {items.map((item) => {
              const meta = TYPE_META[item.type] || { icon: "bell", color: "var(--text-3)" }
              const isUnread = !item.read_at
              return (
                <li
                  key={item.id}
                  className={"notif-item" + (isUnread ? " unread" : "")}
                  onClick={() => handleClickItem(item)}
                >
                  <div
                    className="notif-icon"
                    style={{ background: meta.color + "22", color: meta.color }}
                  >
                    <Icon name={meta.icon} size={14} />
                  </div>
                  <div className="notif-body">
                    <div className="notif-title">{item.title}</div>
                    {item.body && (
                      <div className="notif-sub">{item.body}</div>
                    )}
                    <div className="notif-time">{relTime(item.created_at, lang)}</div>
                  </div>
                  {isUnread && <span className="notif-dot" />}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
